// Email Express 2.0 — direct-render editor (Task #2701).
//
// Renders the persisted plain HTML directly into the DOM (no iframe, no
// React remount of generated markup): the container's innerHTML is set
// exactly once per distinct `html` value, then individual leaf text
// elements are made contenteditable in place. Saving reads the live DOM
// back out (innerHTML) rather than re-rendering from state.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, Mail, Sparkles,
  Save, Send, History, AlertTriangle, CheckCircle2, ArrowLeft,
  ImagePlus, X, RotateCw, ChevronDown,
} from "lucide-react";
import type {
  Persona, Product, Offer, NarrativeAngle,
  EmailExpress2ContextSnapshot, EmailExpress2Design, EmailExpress2CampaignType,
  EmailExpress2EmailType, EmailExpress2Version,
} from "@shared/schema";
import { selectablePersonas } from "@shared/persona-select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { emailExpress2ErrorMessage } from "@/lib/email-express-2-errors";
import {
  EmailExpress2RevisionConversation,
  EmailExpress2StaticInputs,
} from "@/components/email-express-2-editor-panels";

const CAMPAIGN_TYPE_LABELS: Record<EmailExpress2CampaignType, string> = {
  education: "Education",
  sale: "Sale",
  product_highlight: "Product highlight",
  review_led: "Review-led",
  announcement: "Announcement",
  welcome: "Welcome",
  cart_abandonment: "Cart abandonment",
  browse_abandonment: "Browse abandonment",
  retention: "Retention",
};

const EMAIL_TYPE_LABELS: Record<EmailExpress2EmailType, string> = {
  promotional: "Promotional",
  newsletter: "Newsletter",
  announcement: "Announcement",
  abandoned_cart: "Abandoned cart",
  welcome: "Welcome",
  post_purchase: "Post-purchase",
  re_engagement: "Re-engagement",
};

const VERSION_KIND_LABELS: Record<string, string> = {
  create: "Initial design",
  chat_revision: "Chat revision",
  manual_save: "Manual edit",
};

interface ContextState {
  productId: string;
  personaId: string;
  angleMode: "saved" | "custom";
  narrativeAngleId: string;
  customAngle: string;
  offerId: string;
  campaignType: EmailExpress2CampaignType;
  emailType: EmailExpress2EmailType;
  briefText: string;
  referenceImageUrl: string;
  ctaUrl: string;
}

interface KlaviyoPreflight {
  subject?: string;
  previewText?: string;
  mode?: string | null;
  audience?: string | { id?: string; name?: string; label?: string } | null;
  audienceName?: string | null;
  audienceLabel?: string | null;
  trigger?: string | { id?: string; name?: string; label?: string } | null;
  triggerName?: string | null;
  triggerLabel?: string | null;
  degraded?: boolean;
  degradedWarning?: string | null;
  warning?: string | null;
  message?: string | null;
}

function preflightLabel(
  value: KlaviyoPreflight["audience"] | KlaviyoPreflight["trigger"],
): string {
  if (typeof value === "string") return value;
  return value?.name || value?.label || value?.id || "";
}

// Client-mirror of the server's revision-image constraints (the server is
// authoritative; these exist for fast local feedback only).
const REVISION_IMAGE_ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif";
const REVISION_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const REVISION_IMAGE_MAX_COUNT = 5;
const REVISION_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);

interface StagedAttachment {
  /** Local list identity — stable across upload retries. */
  localId: string;
  /** Server row id once the upload has completed. */
  id?: string;
  fileName: string;
  previewUrl: string;
  status: "uploading" | "done" | "error";
  progress: number; // 0..100
  error?: string;
  /** Kept for retrying a failed upload; absent for hydrated pending rows. */
  file?: File;
}

interface VersionAttachmentMeta {
  id: string;
  fileName: string;
  url: string;
  contentType: string;
  sizeBytes: number;
}

function contextFromDesign(design: EmailExpress2Design): ContextState {
  return {
    productId: design.productId || "",
    personaId: design.personaId || "",
    angleMode: design.narrativeAngleId ? "saved" : "custom",
    narrativeAngleId: design.narrativeAngleId || "",
    customAngle: design.customAngle || "",
    offerId: design.offerId || "",
    campaignType: design.campaignType as EmailExpress2CampaignType,
    emailType: design.emailType as EmailExpress2EmailType,
    briefText: design.briefText || "",
    referenceImageUrl: design.referenceImageUrl || "",
    ctaUrl: design.ctaUrl || "",
  };
}

// Leaf elements (no element children) with real text content are made
// individually editable — never the container as a whole, so structural
// nodes (rows, tables, images) stay fixed.
const NON_EDITABLE_TAGS = new Set(["IMG", "BR", "HR", "INPUT", "SELECT", "TEXTAREA"]);
function setEditableTextElements(root: HTMLElement, editable: boolean) {
  if (!editable) {
    root.querySelectorAll("[data-ee2-editable]").forEach((el) => {
      el.removeAttribute("contenteditable");
      el.removeAttribute("data-ee2-editable");
    });
    return;
  }
  root.querySelectorAll<HTMLElement>("*").forEach((el) => {
    if (NON_EDITABLE_TAGS.has(el.tagName)) return;
    if (el.children.length > 0) return;
    if (!(el.textContent || "").trim()) return;
    el.setAttribute("contenteditable", "true");
    el.setAttribute("data-ee2-editable", "true");
  });
}

// Strips the runtime-only editing affordances back out before persisting —
// the saved HTML should look exactly like what generation produced, plus
// the user's text edits.
function serializeForSave(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll("[data-ee2-editable]").forEach((el) => {
    el.removeAttribute("contenteditable");
    el.removeAttribute("data-ee2-editable");
  });
  return clone.innerHTML;
}

export default function EmailExpress2EditorPage() {
  const { brandId, id } = useParams<{ brandId: string; id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const contentRef = useRef<HTMLDivElement>(null);
  const renderedHtmlRef = useRef<string | null>(null);
  const contextHydratedRef = useRef(false);

  const [context, setContext] = useState<ContextState | null>(null);
  const [chatInstruction, setChatInstruction] = useState("");
  const [viewingVersion, setViewingVersion] = useState<EmailExpress2Version | null>(null);
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const metadataHydratedRef = useRef<string | null>(null);
  const [klaviyoPreflight, setKlaviyoPreflight] = useState<KlaviyoPreflight | null>(null);
  const [sendConfirmationOpen, setSendConfirmationOpen] = useState(false);
  const [footerSettingsOpen, setFooterSettingsOpen] = useState(false);
  const [footerAddress, setFooterAddress] = useState("");
  const [footerUnsubUrl, setFooterUnsubUrl] = useState("");
  const [footerSettingsRequired, setFooterSettingsRequired] = useState(false);
  const [pendingRevision, setPendingRevision] = useState<{ instruction: string; attachments: VersionAttachmentMeta[] } | null>(null);
  const prevDesignStatusRef = useRef<string | undefined>(undefined);
  const utilityExpansionHydratedRef = useRef<string | null>(null);
  const [deliveryDetailsOpen, setDeliveryDetailsOpen] = useState(false);
  const [klaviyoOpen, setKlaviyoOpen] = useState(false);

  const { data: design, isLoading: designLoading } = useQuery<EmailExpress2Design>({
    queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}`],
    enabled: !!brandId && !!id,
    refetchInterval: (query) => (query.state.data?.status === "generating" ? 3000 : false),
  });
  const deliveryLocked = design?.klaviyoStatus === "sending" || design?.klaviyoStatus === "sent";

  useEffect(() => {
    if (!design || design.status !== "ready" || utilityExpansionHydratedRef.current === design.id) return;
    utilityExpansionHydratedRef.current = design.id;
    setDeliveryDetailsOpen(!design.subject?.trim() || !design.previewText?.trim());
    setKlaviyoOpen(design.klaviyoStatus !== "sending" && design.klaviyoStatus !== "sent");
  }, [design]);

  const { data: versions = [] } = useQuery<EmailExpress2Version[]>({
    queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/versions`],
    enabled: !!brandId && !!id,
  });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: personasRaw = [] } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const personas = useMemo(() => selectablePersonas(personasRaw), [personasRaw]);
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });
  const { data: offers = [] } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });
  const footerSettingsKey = `/api/brands/${brandId}/email-express/settings`;
  const {
    data: footerSettings,
    isLoading: footerSettingsLoading,
  } = useQuery<{ physicalAddress: string; unsubscribeUrl: string }>({
    queryKey: [footerSettingsKey],
    enabled: !!brandId,
  });
  useEffect(() => {
    if (!footerSettingsOpen || !footerSettings) return;
    setFooterAddress(footerSettings.physicalAddress ?? "");
    setFooterUnsubUrl(footerSettings.unsubscribeUrl ?? "");
  }, [footerSettingsOpen, footerSettings]);

  const saveFooterSettingsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", footerSettingsKey, {
        physicalAddress: footerAddress.trim(),
        unsubscribeUrl: footerUnsubUrl.trim(),
      });
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [footerSettingsKey] });
      setFooterSettingsRequired(false);
      setFooterSettingsOpen(false);
      toast({
        title: "Footer settings saved",
        description: "Your Klaviyo delivery settings are ready to use.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't save footer settings",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Hydrate the editable context panel from the design exactly once — later
  // background polls (during generation) must never clobber in-progress edits.
  useEffect(() => {
    contextHydratedRef.current = false;
    metadataHydratedRef.current = null;
    setKlaviyoPreflight(null);
    setSendConfirmationOpen(false);
  }, [id]);
  useEffect(() => {
    if (design && !contextHydratedRef.current) {
      setContext(contextFromDesign(design));
      contextHydratedRef.current = true;
    }
  }, [design]);
  useEffect(() => {
    if (!design) return;
    const key = `${design.id}:${design.updatedAt || ""}:${design.subject || ""}:${design.previewText || ""}`;
    if (metadataHydratedRef.current === key) return;
    setSubject(design.subject || "");
    setPreviewText(design.previewText || "");
    metadataHydratedRef.current = key;
  }, [design]);

  // Render the persisted HTML directly into the DOM exactly once per
  // distinct value — never remounted as a React tree. Deliberately has NO
  // dependency array: it must re-check on every render (cheap, thanks to
  // the guards below) because `contentRef` only mounts once `context` has
  // hydrated, which happens a render AFTER `design.html` first becomes
  // available — a `[design?.html]`-only effect would fire once against a
  // still-null ref and never get a second chance to write the DOM.
  useEffect(() => {
    if (!design?.html) return;
    if (!contentRef.current) return; // not mounted yet — retry on the next render
    if (renderedHtmlRef.current !== design.html) {
      contentRef.current.innerHTML = design.html;
      renderedHtmlRef.current = design.html;
    }
    setEditableTextElements(contentRef.current, !deliveryLocked && design.status !== "generating");
  });

  const invalidateDesign = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}`] });
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/versions`] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!contentRef.current) throw new Error("Nothing to save");
      const html = serializeForSave(contentRef.current);
      const res = await apiRequest("PATCH", `/api/brands/${brandId}/email-express-2/designs/${id}/html`, { html });
      return { html, response: await res.json() };
    },
    onSuccess: ({ html }) => {
      renderedHtmlRef.current = html;
      invalidateDesign();
      toast({ title: "Saved", description: "Your edit is persisted." });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't save", description: e?.message || "Please try again.", variant: "destructive" });
    },
  });

  // ── Revision image attachments (Task #2745) ─────────────────────────
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Server ids the user just removed — the DELETE may still be in flight
  // when a refetch returns the row, so reconciliation must skip them.
  const removedServerIdsRef = useRef(new Set<string>());

  // The server's pending list is authoritative for uploaded-but-unsent
  // images: it seeds the composer after a reload AND restores attachments
  // that a failed generation released back to pending.
  const { data: pendingImages } = useQuery<{ images: { id: string; fileName: string; url: string }[] }>({
    queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/revision-images`],
    enabled: !!brandId && !!id,
  });
  useEffect(() => {
    removedServerIdsRef.current = new Set();
    setAttachments([]);
  }, [id]);
  // Continuous reconcile (not one-shot): local rows without a server id
  // (uploading/error) are kept as-is; rows WITH a server id mirror the
  // server list — added when new, dropped when claimed/deleted elsewhere.
  useEffect(() => {
    if (!pendingImages) return;
    const serverIds = new Set(pendingImages.images.map((img) => img.id));
    // Once the server stops returning a removed id, forget it.
    for (const removedId of [...removedServerIdsRef.current]) {
      if (!serverIds.has(removedId)) removedServerIdsRef.current.delete(removedId);
    }
    setAttachments((prev) => {
      const kept = prev.filter((a) => !a.id || serverIds.has(a.id));
      const known = new Set(kept.map((a) => a.id).filter(Boolean));
      const added = pendingImages.images
        .filter((img) => !known.has(img.id) && !removedServerIdsRef.current.has(img.id))
        .map((img) => ({
          localId: img.id,
          id: img.id,
          fileName: img.fileName,
          previewUrl: img.url,
          status: "done" as const,
          progress: 100,
        }));
      if (added.length === 0 && kept.length === prev.length) return prev;
      return [...kept, ...added];
    });
  }, [pendingImages]);

  // When a generation settles (failed OR ready), refetch the pending list:
  // a failure releases the claimed attachments back to pending and they
  // must reappear in the composer without a reload.
  const designStatus = design?.status;
  useEffect(() => {
    pendingRevision?.attachments.forEach((attachment) => {
      if (attachment.url.startsWith("blob:")) URL.revokeObjectURL(attachment.url);
    });
    setPendingRevision(null);
    prevDesignStatusRef.current = undefined;
  }, [id]);
  useEffect(() => {
    const prev = prevDesignStatusRef.current;
    prevDesignStatusRef.current = designStatus;
    if (prev === "generating" && (designStatus === "failed" || designStatus === "ready")) {
      pendingRevision?.attachments.forEach((attachment) => {
        if (attachment.url.startsWith("blob:")) URL.revokeObjectURL(attachment.url);
      });
      setPendingRevision(null);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/revision-images`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/versions`],
      });
    }
  }, [designStatus, brandId, id, queryClient, pendingRevision]);

  const patchAttachment = (localId: string, patch: Partial<StagedAttachment>) =>
    setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, ...patch } : a)));

  // Removal during an in-flight upload must not leave a server-side row
  // behind: track each XHR so remove can abort it, and remember removed
  // localIds so an upload that already won the race gets its created row
  // deleted the moment its response lands.
  const uploadXhrsRef = useRef(new Map<string, XMLHttpRequest>());
  const removedLocalIdsRef = useRef(new Set<string>());

  const deleteServerImage = (imageId: string) => {
    apiRequest("DELETE", `/api/brands/${brandId}/email-express-2/designs/${id}/revision-images/${imageId}`)
      .catch(() => { /* best-effort: the row stays pending server-side */ });
  };

  // XHR (not fetch) for real upload progress; one request per file so each
  // row gets its own progress bar and failure state.
  const uploadAttachment = (localId: string, file: File) => {
    removedLocalIdsRef.current.delete(localId);
    patchAttachment(localId, { status: "uploading", progress: 0, error: undefined });
    const form = new FormData();
    form.append("files", file);
    const xhr = new XMLHttpRequest();
    uploadXhrsRef.current.set(localId, xhr);
    xhr.open("POST", `/api/brands/${brandId}/email-express-2/designs/${id}/revision-images`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) patchAttachment(localId, { progress: Math.round((e.loaded / e.total) * 100) });
    };
    xhr.onload = () => {
      uploadXhrsRef.current.delete(localId);
      try {
        const body = JSON.parse(xhr.responseText || "{}");
        const createdId: string | undefined = body.images?.[0]?.id;
        if (removedLocalIdsRef.current.has(localId)) {
          // Removed while uploading but the request completed anyway — clean
          // up the row it just created so it can't reappear or hold quota.
          removedLocalIdsRef.current.delete(localId);
          if (createdId) deleteServerImage(createdId);
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300 && createdId) {
          patchAttachment(localId, { id: createdId, status: "done", progress: 100 });
        } else {
          patchAttachment(localId, { status: "error", error: body.error || "Upload failed" });
        }
      } catch {
        if (!removedLocalIdsRef.current.delete(localId)) {
          patchAttachment(localId, { status: "error", error: "Upload failed" });
        }
      }
    };
    xhr.onerror = () => {
      uploadXhrsRef.current.delete(localId);
      if (!removedLocalIdsRef.current.delete(localId)) {
        patchAttachment(localId, { status: "error", error: "Network error during upload" });
      }
    };
    xhr.onabort = () => {
      uploadXhrsRef.current.delete(localId);
    };
    xhr.send(form);
  };

  const addFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setAttachments((prev) => {
      const room = REVISION_IMAGE_MAX_COUNT - prev.length;
      if (files.length > room) {
        toast({
          title: "Too many images",
          description: `A revision can include at most ${REVISION_IMAGE_MAX_COUNT} images.`,
          variant: "destructive",
        });
      }
      const accepted: StagedAttachment[] = [];
      for (const file of files.slice(0, Math.max(room, 0))) {
        if (!REVISION_IMAGE_TYPES.has(file.type)) {
          toast({ title: "Unsupported file", description: `${file.name} is not a PNG, JPG, WebP or GIF.`, variant: "destructive" });
          continue;
        }
        if (file.size > REVISION_IMAGE_MAX_BYTES) {
          toast({ title: "Image too large", description: `${file.name} is over 10 MB.`, variant: "destructive" });
          continue;
        }
        const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        accepted.push({
          localId,
          fileName: file.name,
          previewUrl: URL.createObjectURL(file),
          status: "uploading",
          progress: 0,
          file,
        });
        // Kick the upload after state settles.
        setTimeout(() => uploadAttachment(localId, file), 0);
      }
      return [...prev, ...accepted];
    });
  };

  const removeAttachment = (att: StagedAttachment) => {
    setAttachments((prev) => prev.filter((a) => a.localId !== att.localId));
    if (att.previewUrl.startsWith("blob:")) URL.revokeObjectURL(att.previewUrl);
    if (att.id) removedServerIdsRef.current.add(att.id);
    if (att.status === "uploading") {
      // Abort the in-flight request; if it already completed server-side,
      // the onload handler sees the removed flag and deletes the row.
      removedLocalIdsRef.current.add(att.localId);
      uploadXhrsRef.current.get(att.localId)?.abort();
    }
    if (att.id) deleteServerImage(att.id);
  };

  const attachmentsUploading = attachments.some((a) => a.status === "uploading");
  const attachmentsFailed = attachments.some((a) => a.status === "error");

  const reviseMutation = useMutation({
    mutationFn: async () => {
      const attachmentIds = attachments.filter((a) => a.status === "done" && a.id).map((a) => a.id!);
      const body = {
        attachmentIds,
        instruction: chatInstruction.trim(),
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/email-express-2/designs/${id}/revise`, body);
      await res.json();
      return {
        attachmentIds,
        instruction: chatInstruction.trim(),
        sentAttachments: attachments
          .filter((a) => a.status === "done" && a.id)
          .map((a) => ({ id: a.id!, fileName: a.fileName, url: a.previewUrl, contentType: "", sizeBytes: 0 })),
      };
    },
    onSuccess: ({ attachmentIds, instruction, sentAttachments }) => {
      setPendingRevision({ instruction, attachments: sentAttachments });
      queryClient.setQueryData<EmailExpress2Design>(
        [`/api/brands/${brandId}/email-express-2/designs/${id}`],
        (current) => current ? { ...current, status: "generating" } : current,
      );
      setChatInstruction("");
      // ONLY the attachments the accepted revise actually claimed leave the
      // staging list — anything added mid-request stays staged.
      const sent = new Set(attachmentIds);
      setAttachments((prev) => {
        const remaining = prev.filter((a) => !(a.id && sent.has(a.id)));
        return remaining;
      });
      invalidateDesign();
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/revision-images`] });
      toast({ title: "Revising design", description: "Updating your email design…" });
    },
    onError: (e: any) => {
      // Typed feedback and staged attachments are both preserved.
      toast({ title: "Couldn't send revision", description: emailExpress2ErrorMessage(e), variant: "destructive" });
    },
  });

  const saveDeliveryMetadata = async () => {
    const res = await apiRequest(
      "PATCH",
      `/api/brands/${brandId}/email-express-2/designs/${id}/delivery-metadata`,
      { subject: subject.trim(), previewText: previewText.trim() },
    );
    return res.json();
  };

  const deliveryMetadataMutation = useMutation({
    mutationFn: saveDeliveryMetadata,
    onSuccess: () => {
      invalidateDesign();
      toast({ title: "Delivery details saved", description: "Subject and preview text are ready." });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't save delivery details", description: e?.message || "Please try again.", variant: "destructive" });
    },
  });

  const preflightMutation = useMutation({
    mutationFn: async () => {
      const metadataChanged =
        subject.trim() !== (design?.subject || "")
        || previewText.trim() !== (design?.previewText || "");
      if (metadataChanged) await saveDeliveryMetadata();
      const res = await apiRequest(
        "GET",
        `/api/brands/${brandId}/email-express-2/designs/${id}/klaviyo/preflight`,
      );
      const data = await res.json();
      return (data.preflight || data) as KlaviyoPreflight;
    },
    onSuccess: (preflight) => {
      setFooterSettingsRequired(false);
      setKlaviyoPreflight(preflight);
      setSendConfirmationOpen(true);
      invalidateDesign();
    },
    onError: (e: any) => {
      const message = e?.message || "Please try again.";
      const missingPhysicalAddress = /physical (mailing )?address is required/i.test(message);
      setFooterSettingsRequired(missingPhysicalAddress);
      toast({
        title: "Klaviyo preflight failed",
        description: missingPhysicalAddress
          ? "Add your physical mailing address in Email footer settings, save it, then try sending again."
          : message,
        variant: "destructive",
      });
    },
  });

  const klaviyoSendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express-2/designs/${id}/klaviyo/send`,
        {
          audienceId:
            typeof klaviyoPreflight?.audience === "object"
              ? klaviyoPreflight.audience?.id || null
              : null,
          trigger:
            typeof klaviyoPreflight?.trigger === "object"
              ? klaviyoPreflight.trigger
              : null,
        },
      );
      return await res.json();
    },
    onSuccess: (updated: any) => {
      setSendConfirmationOpen(false);
      const mode =
        updated?.klaviyoMode
        || updated?.mode
        || updated?.result?.mode
        || updated?.klaviyo?.mode
        || klaviyoPreflight?.mode;
      const isFlow = mode === "flow";
      const isFlowTemplate = mode === "flow_template";
      const isTemplate = mode === "template";
      toast({
        title: isFlow
          ? "Draft flow created in Klaviyo"
          : isFlowTemplate
            ? "Flow template saved in Klaviyo"
            : isTemplate
              ? "Template saved in Klaviyo"
              : "Draft campaign created in Klaviyo",
        description:
          updated?.message
          || updated?.klaviyo?.message
          || (isFlow
            ? "Review the trigger in Klaviyo, then turn the flow on."
            : isFlowTemplate
              ? "Klaviyo could not create the flow automatically. Use the saved template to finish the flow manually."
              : isTemplate
                ? "No audience was available, so the email was saved as a reusable template."
                : "Review and schedule the draft campaign in Klaviyo."),
      });
      invalidateDesign();
    },
    onError: (e: any) => {
      toast({ title: "Send failed", description: e?.message || "Please try again.", variant: "destructive" });
      invalidateDesign();
    },
  });

  const { data: versionDetail } = useQuery<EmailExpress2Version>({
    queryKey: [`/api/brands/${brandId}/email-express-2/designs/${id}/versions/${viewingVersion?.id}`],
    enabled: !!viewingVersion,
  });

  if (designLoading || !design || !context) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isGenerating = design.status === "generating";
  const isFailed = design.status === "failed";
  const isReady = design.status === "ready";
  const klaviyoStatus = design.klaviyoStatus || "not_sent";
  const klaviyoAlreadySubmitted = klaviyoStatus === "sending" || klaviyoStatus === "sent";
  const editingLocked = isGenerating || klaviyoAlreadySubmitted;
  // While the revise request is in flight, adding attachments would race the
  // server-side claim (a just-added image was not in the submitted ids) —
  // lock the picker/drop zone as strictly as a generating design.
  const composerLocked = editingLocked || reviseMutation.isPending;
  const klaviyoBusy = preflightMutation.isPending || klaviyoSendMutation.isPending;
  const audienceLabel =
    klaviyoPreflight?.audienceName
    || klaviyoPreflight?.audienceLabel
    || preflightLabel(klaviyoPreflight?.audience);
  const triggerLabel =
    klaviyoPreflight?.triggerName
    || klaviyoPreflight?.triggerLabel
    || preflightLabel(klaviyoPreflight?.trigger);
  const originalSnapshot = design.contextSnapshot as EmailExpress2ContextSnapshot | null;
  const productLabel =
    originalSnapshot?.product?.name
    || products.find((item) => item.id === context.productId)?.name
    || null;
  const personaLabel =
    originalSnapshot?.persona?.name
    || personas.find((item) => item.id === context.personaId)?.name
    || null;
  const savedAngleLabel =
    originalSnapshot?.narrativeAngle?.name
    || angles.find((item) => item.id === context.narrativeAngleId)?.name
    || null;
  const offerLabel =
    originalSnapshot?.offer?.name
    || offers.find((item) => item.id === context.offerId)?.name
    || null;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-4 pt-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate(`/brands/${brandId}/email-express-2`)}
          data-testid="button-back-email-express-2"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back
        </Button>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-[320px_1fr_360px] gap-4 p-4">
      {/* Left: immutable campaign inputs */}
      <div className="overflow-y-auto space-y-4 pr-1">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Inputs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EmailExpress2StaticInputs
              product={productLabel}
              persona={personaLabel}
              angle={savedAngleLabel || context.customAngle || null}
              offer={offerLabel}
              campaignType={CAMPAIGN_TYPE_LABELS[context.campaignType]}
              emailType={EMAIL_TYPE_LABELS[context.emailType]}
              brief={context.briefText}
              ctaUrl={context.ctaUrl || null}
              referenceImageUrl={context.referenceImageUrl || null}
            />
          </CardContent>
        </Card>
      </div>

      {/* Center: fake-inbox rendering surface */}
      <div className="overflow-y-auto flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isGenerating && (
              <Badge variant="secondary" className="gap-1" data-testid="badge-status-generating">
                <Loader2 className="h-3 w-3 animate-spin" /> Generating…
              </Badge>
            )}
            {isFailed && (
              <Badge variant="destructive" className="gap-1" data-testid="badge-status-failed">
                <AlertTriangle className="h-3 w-3" /> Failed
              </Badge>
            )}
            {design.status === "ready" && (
              <Badge variant="outline" className="gap-1" data-testid="badge-status-ready">
                <CheckCircle2 className="h-3 w-3" /> Ready
              </Badge>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={editingLocked || !design.html || saveMutation.isPending}
            data-testid="button-save"
          >
            {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
        </div>

        {isFailed && design.error && (
          <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="text-generation-error">
            {emailExpress2ErrorMessage(design.error)}
          </div>
        )}

        {/* Fake-inbox chrome around the plain-HTML surface. */}
        <div className="flex-1 min-h-0 rounded-lg border bg-muted/30 p-4 overflow-y-auto">
          {/* iPhone 17 CSS viewport width (1206 physical px at 3x). */}
          <div className="mx-auto w-[402px] max-w-full rounded-md border bg-background shadow-sm overflow-hidden">
            <div className="border-b px-4 py-3 space-y-1 bg-muted/40">
              <div className="text-[11px] text-muted-foreground">From: Your Brand &lt;hello@example.com&gt;</div>
              <div className="text-sm font-semibold" data-testid="text-fake-subject">
                {subject || `${EMAIL_TYPE_LABELS[context.emailType]} — ${products.find((p) => p.id === context.productId)?.name || "Email"}`}
              </div>
              {previewText && <div className="text-xs text-muted-foreground" data-testid="text-fake-preview">{previewText}</div>}
            </div>
            <div className="p-0" data-testid="container-email-html">
              {!design.html && !isGenerating && (
                <div className="p-8 text-center text-sm text-muted-foreground">No content yet.</div>
              )}
              <div ref={contentRef} />
            </div>
          </div>
        </div>
      </div>

      {/* Right: chat + version history */}
      <div className="flex min-h-0 flex-col gap-4 pl-1">
        {isReady && (
          <Card className="shrink-0" data-testid="card-delivery-details">
            <CardHeader className={deliveryDetailsOpen ? "pb-3" : "py-3"}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-expanded={deliveryDetailsOpen}
                aria-controls="ee2-delivery-details-content"
                onClick={() => setDeliveryDetailsOpen((open) => !open)}
                data-testid="button-toggle-delivery-details"
              >
                <Mail className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <CardTitle className="text-sm">Delivery details</CardTitle>
                  <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground" data-testid="summary-delivery-details">
                    {subject.trim() && previewText.trim() ? "Subject and preview saved" : "Subject and preview required"}
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${deliveryDetailsOpen ? "rotate-180" : ""}`} />
              </button>
            </CardHeader>
            {deliveryDetailsOpen && <CardContent id="ee2-delivery-details-content" className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ee2-subject">Subject</label>
                <Input
                  id="ee2-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject"
                  maxLength={255}
                  disabled={deliveryMetadataMutation.isPending || klaviyoAlreadySubmitted}
                  data-testid="input-subject"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ee2-preview-text">Preview text</label>
                <Textarea
                  id="ee2-preview-text"
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  placeholder="Inbox preview text"
                  maxLength={160}
                  rows={2}
                  disabled={deliveryMetadataMutation.isPending || klaviyoAlreadySubmitted}
                  data-testid="input-preview-text"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => deliveryMetadataMutation.mutate()}
                disabled={
                  !subject.trim()
                  || !previewText.trim()
                  || deliveryMetadataMutation.isPending
                  || klaviyoAlreadySubmitted
                }
                data-testid="button-save-delivery-metadata"
              >
                {deliveryMetadataMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Save delivery details
              </Button>
            </CardContent>}
          </Card>
        )}

        {isReady && (
          <Card className="shrink-0" data-testid="card-send-klaviyo">
            <CardHeader className={klaviyoOpen ? "pb-3" : "py-3"}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-expanded={klaviyoOpen}
                aria-controls="ee2-klaviyo-content"
                onClick={() => setKlaviyoOpen((open) => !open)}
                data-testid="button-toggle-klaviyo"
              >
                <Send className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <CardTitle className="text-sm">Send to Klaviyo</CardTitle>
                  <span className={`mt-0.5 block truncate text-xs font-normal ${klaviyoStatus === "failed" || footerSettingsRequired ? "text-destructive" : "text-muted-foreground"}`} data-testid="summary-klaviyo">
                    {footerSettingsRequired
                      ? "Footer settings required"
                      : klaviyoStatus === "sending"
                        ? "Sending…"
                        : klaviyoStatus === "sent"
                          ? "Sent to Klaviyo"
                          : klaviyoStatus === "failed"
                            ? "Send failed — action required"
                            : "Ready to review and send"}
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${klaviyoOpen ? "rotate-180" : ""}`} />
              </button>
            </CardHeader>
            {klaviyoOpen && <CardContent id="ee2-klaviyo-content" className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setFooterSettingsOpen(true)}
                data-testid="button-footer-settings"
              >
                Email footer settings
              </Button>
              {klaviyoStatus !== "not_sent" && (
                <Badge
                  variant={klaviyoStatus === "failed" ? "destructive" : "secondary"}
                  data-testid="badge-klaviyo-status"
                >
                  {klaviyoStatus === "sending" ? "Sending…" : klaviyoStatus === "sent" ? "Sent" : "Send failed"}
                </Badge>
              )}
              {design.klaviyoMode && (
                <p className="text-xs text-muted-foreground" data-testid="text-klaviyo-mode">
                  Klaviyo mode: {design.klaviyoMode.replace(/_/g, " ")}
                </p>
              )}
              {design.klaviyoError && (
                <p className="text-xs text-destructive" data-testid="text-klaviyo-error">{design.klaviyoError}</p>
              )}
              {footerSettingsRequired && (
                <div
                  className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs"
                  data-testid="alert-footer-settings-required"
                >
                  <p className="font-medium text-destructive">A physical mailing address is required.</p>
                  <p className="mt-1 text-muted-foreground">
                    Open Email footer settings, add the brand's address, save it, then try sending again.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => setFooterSettingsOpen(true)}
                    data-testid="button-fix-footer-settings"
                  >
                    Open footer settings
                  </Button>
                </div>
              )}
              <Button
                className="w-full"
                size="sm"
                onClick={() => preflightMutation.mutate()}
                disabled={!subject.trim() || !previewText.trim() || klaviyoAlreadySubmitted || klaviyoBusy}
                data-testid="button-send-klaviyo"
              >
                {preflightMutation.isPending
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Send className="h-3.5 w-3.5 mr-1.5" />}
                {klaviyoAlreadySubmitted
                  ? (klaviyoStatus === "sending" ? "Sending…" : "Sent to Klaviyo")
                  : "Send to Klaviyo"}
              </Button>
            </CardContent>}
          </Card>
        )}

        <Card className="order-1 shrink-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Send className="h-4 w-4" /> Next revision
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div
              className={`rounded-md ${dragActive ? "ring-2 ring-primary/60" : ""}`}
              onDragOver={(e) => { e.preventDefault(); if (!composerLocked) setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                if (!composerLocked && e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
              }}
              data-testid="dropzone-revision-images"
            >
              <Textarea
                value={chatInstruction}
                onChange={(e) => setChatInstruction(e.target.value)}
                rows={4}
                placeholder="e.g. 'Make the headline bigger and swap the CTA copy to Shop Now'"
                disabled={editingLocked}
                data-testid="input-chat-instruction"
              />
            </div>
            {attachments.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="list-revision-attachments">
                {attachments.map((att) => (
                  <div
                    key={att.localId}
                    className="relative rounded-md border overflow-hidden group"
                    data-testid={`attachment-${att.localId}`}
                  >
                    <img src={att.previewUrl} alt={att.fileName} className="h-16 w-full object-cover" />
                    {att.status === "uploading" && (
                      <div className="absolute inset-0 bg-background/70 flex flex-col items-center justify-center gap-1">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-[10px]" data-testid={`attachment-progress-${att.localId}`}>{att.progress}%</span>
                      </div>
                    )}
                    {att.status === "error" && (
                      <div className="absolute inset-0 bg-destructive/20 flex flex-col items-center justify-center gap-1 p-1">
                        <span className="text-[9px] text-center text-destructive line-clamp-2">{att.error || "Upload failed"}</span>
                        {att.file && (
                          <Button
                            type="button" size="sm" variant="secondary" className="h-5 px-1.5 text-[10px]"
                            onClick={() => uploadAttachment(att.localId, att.file!)}
                            data-testid={`button-retry-attachment-${att.localId}`}
                          >
                            <RotateCw className="h-3 w-3 mr-0.5" /> Retry
                          </Button>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      className="absolute top-0.5 right-0.5 rounded-full bg-background/80 border p-0.5 opacity-80 hover:opacity-100"
                      onClick={() => removeAttachment(att)}
                      aria-label={`Remove ${att.fileName}`}
                      data-testid={`button-remove-attachment-${att.localId}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="px-1 py-0.5 text-[9px] truncate bg-background/90">{att.fileName}</div>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={REVISION_IMAGE_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                if (!composerLocked && e.target.files?.length) addFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="input-revision-images"
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={composerLocked || attachments.length >= REVISION_IMAGE_MAX_COUNT}
                data-testid="button-add-revision-image"
              >
                <ImagePlus className="h-3.5 w-3.5 mr-1.5" /> Add image
              </Button>
              <span className="text-[10px] text-muted-foreground">
                {attachments.length > 0
                  ? `${attachments.length}/${REVISION_IMAGE_MAX_COUNT} images`
                  : "PNG, JPG, WebP or GIF — drag in or pick"}
              </span>
            </div>
            <Button
              className="w-full"
              size="sm"
              onClick={() => reviseMutation.mutate()}
              disabled={
                editingLocked || !chatInstruction.trim()
                || reviseMutation.isPending || attachmentsUploading || attachmentsFailed
              }
              data-testid="button-send-revision"
            >
              {reviseMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              {attachmentsUploading ? "Uploading images…" : "Send"}
            </Button>
            {attachmentsFailed && (
              <p className="text-[10px] text-destructive" data-testid="text-attachment-error-hint">
                Remove or retry the failed image before sending.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="order-2 flex min-h-0 flex-1 flex-col overflow-hidden">
          <CardHeader className="shrink-0 pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="h-4 w-4" /> Revision conversation
            </CardTitle>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col">
            <EmailExpress2RevisionConversation
              versions={versions}
              pendingRevision={pendingRevision}
              isGenerating={isGenerating}
              isFailed={isFailed}
              onOpenVersion={setViewingVersion}
            />
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!viewingVersion} onOpenChange={(open) => !open && setViewingVersion(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingVersion ? VERSION_KIND_LABELS[viewingVersion.kind] || viewingVersion.kind : ""}
            </DialogTitle>
          </DialogHeader>
          {viewingVersion?.chatInstruction && (
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
              <span className="font-medium">Instruction: </span>
              {viewingVersion.chatInstruction}
            </div>
          )}
          {Array.isArray(viewingVersion?.attachmentsJson) && viewingVersion.attachmentsJson.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="version-dialog-attachments">
              {(viewingVersion.attachmentsJson as VersionAttachmentMeta[]).map((att) => (
                <a key={att.id} href={att.url} target="_blank" rel="noreferrer" className="block">
                  <img src={att.url} alt={att.fileName} title={att.fileName} className="h-16 w-16 rounded-md object-cover border" />
                </a>
              ))}
            </div>
          )}
          <div className="rounded-md border bg-background overflow-hidden">
            {versionDetail ? (
              <div dangerouslySetInnerHTML={{ __html: versionDetail.html }} data-testid="container-version-preview" />
            ) : (
              <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={footerSettingsOpen} onOpenChange={setFooterSettingsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-footer-settings">
          <DialogHeader>
            <DialogTitle>Email footer settings</DialogTitle>
            <DialogDescription>
              Shared by this brand's emails and checked before Klaviyo delivery.
            </DialogDescription>
          </DialogHeader>
          {footerSettingsLoading ? (
            <div className="flex items-center justify-center py-8" data-testid="loading-footer-settings">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="ee2-footer-address">Physical address</Label>
                <Textarea
                  id="ee2-footer-address"
                  value={footerAddress}
                  onChange={(e) => setFooterAddress(e.target.value)}
                  placeholder={"123 Main St\nSpringfield, CA 90000"}
                  rows={3}
                  data-testid="input-footer-address"
                />
                <p className="text-xs text-muted-foreground">
                  A physical mailing address is required for marketing-email compliance.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ee2-footer-unsubscribe">Unsubscribe URL</Label>
                <Input
                  id="ee2-footer-unsubscribe"
                  value={footerUnsubUrl}
                  onChange={(e) => setFooterUnsubUrl(e.target.value)}
                  placeholder="https://yourstore.com/unsubscribe"
                  data-testid="input-footer-unsubscribe"
                />
                <p className="text-xs text-muted-foreground">
                  Optional — leave blank to use Klaviyo's built-in unsubscribe link.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFooterSettingsOpen(false)}
              disabled={saveFooterSettingsMutation.isPending}
              data-testid="button-footer-settings-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveFooterSettingsMutation.mutate()}
              disabled={footerSettingsLoading || saveFooterSettingsMutation.isPending}
              data-testid="button-footer-settings-save"
            >
              {saveFooterSettingsMutation.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              )}
              Save settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={sendConfirmationOpen}
        onOpenChange={(open) => !klaviyoSendMutation.isPending && setSendConfirmationOpen(open)}
      >
        <DialogContent className="sm:max-w-lg" data-testid="dialog-confirm-klaviyo-send">
          <DialogHeader>
            <DialogTitle>Confirm send to Klaviyo</DialogTitle>
            <DialogDescription>
              Verify the exact inbox copy and destination before creating anything in Klaviyo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs font-medium text-muted-foreground">Subject</div>
              <div className="font-medium whitespace-pre-wrap" data-testid="text-confirm-subject">
                {klaviyoPreflight?.subject || subject}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground">Preview text</div>
              <div className="whitespace-pre-wrap" data-testid="text-confirm-preview-text">
                {klaviyoPreflight?.previewText || previewText}
              </div>
            </div>
            {klaviyoPreflight?.degraded
            || klaviyoPreflight?.mode === "template"
            || klaviyoPreflight?.mode === "flow_template" ? (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-800 dark:text-amber-200"
                data-testid="warning-klaviyo-degraded"
              >
                <div className="font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> Destination details unavailable
                </div>
                <p className="mt-1 text-xs">
                  {klaviyoPreflight.degradedWarning
                    || klaviyoPreflight.warning
                    || klaviyoPreflight.message
                    || "Klaviyo could not verify the audience or trigger. This will be saved in degraded/template mode for manual setup."}
                </p>
              </div>
            ) : (
              <div className="rounded-md bg-muted/50 p-3 space-y-1" data-testid="details-klaviyo-destination">
                {audienceLabel && <div><span className="font-medium">Audience: </span>{audienceLabel}</div>}
                {triggerLabel && <div><span className="font-medium">Trigger: </span>{triggerLabel}</div>}
                {!audienceLabel && !triggerLabel && klaviyoPreflight?.mode && (
                  <div><span className="font-medium">Mode: </span>{klaviyoPreflight.mode.replace(/_/g, " ")}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendConfirmationOpen(false)}
              disabled={klaviyoSendMutation.isPending}
              data-testid="button-cancel-klaviyo-send"
            >
              Cancel
            </Button>
            <Button
              onClick={() => klaviyoSendMutation.mutate()}
              disabled={klaviyoSendMutation.isPending || klaviyoAlreadySubmitted}
              data-testid="button-confirm-klaviyo-send"
            >
              {klaviyoSendMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Confirm and send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
